/**
 * Phase 8 T-22 §27.A — Reusable Assistant intake surface.
 *
 * Visual contract (every instance, no exceptions):
 *   - Centered vertically in the canvas area.
 *   - Emerald agent icon (16 px) + 22 px greeting in zinc-900.
 *   - Auto-grow textarea, max 640 px width, 3 rows → 6 rows.
 *   - Composer footer: optional [🔍 Beta] badge · scope pill · [Submit] emerald.
 *   - Exactly 4 suggestion chips — click auto-fills the textarea.
 *   - Pastel radial emerald gradient (light) / soft purple (dark).
 *     This is the ONLY gradient allowed in the B2B app surface.
 *   - During streaming (§21.G), [Submit] swaps to [Stop] (red-soft) and a
 *     13 px `Evaluating {entityPlural} (N/M)…` meta line appears below
 *     the composer.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  ArrowRightLeft,
  Bell,
  Briefcase,
  Calendar,
  CalendarCheck,
  CheckCircle2,
  Clipboard,
  ClipboardCheck,
  Clock,
  Code2,
  CornerDownLeft,
  FileText,
  FlaskConical,
  Figma,
  Gift,
  Handshake,
  Infinity as InfinityIcon,
  Layers,
  ListChecks,
  Mail,
  Megaphone,
  MessageSquare,
  PiggyBank,
  Phone,
  PhoneCall,
  Plus,
  Reply,
  Repeat,
  Scale,
  Search as SearchIcon,
  Send,
  Snowflake,
  Sparkles,
  Square,
  // Note: Sparkles is kept so the `sparkles` icon key in
  // `intake.config.ts` can map to a real glyph (see ICON_MAP below).
  Star,
  TrendingUp,
  Upload,
  UserPlus,
  Users,
  XCircle,
} from 'lucide-react';

import type { IntakeEntity, IntakeScopePill, IntakeSuggestion } from '../intake.config';
import { AgentMark } from '@shared/components/AgentMark';


/**
 * §27.A — the ONE allowed gradient in the B2B app. Exported as a string so
 * the gradient-audit script can assert this is the single source of truth.
 *
 * Post user-feedback (21-Apr) — refreshed to the soft multi-hue Wrangle
 * blend: pastel peach (top-right), lavender (top-left), cyan (bottom-
 * right), cream (bottom-left), layered over a near-white base. Each
 * radial stop is kept below 0.32 alpha so the gradient reads as ambient
 * chrome rather than competing with UI content. Dark mode uses the same
 * palette at reduced alpha over a deep base for parity without glare.
 */
export const INTAKE_GRADIENT_LIGHT = [
  'radial-gradient(60% 45% at 18% 22%, rgba(232, 213, 250, 0.55) 0%, rgba(232, 213, 250, 0) 70%)',
  'radial-gradient(55% 40% at 82% 18%, rgba(205, 232, 244, 0.60) 0%, rgba(205, 232, 244, 0) 72%)',
  'radial-gradient(70% 50% at 22% 88%, rgba(255, 216, 195, 0.50) 0%, rgba(255, 216, 195, 0) 72%)',
  'radial-gradient(62% 45% at 85% 92%, rgba(255, 226, 205, 0.55) 0%, rgba(255, 226, 205, 0) 72%)',
  'radial-gradient(75% 55% at 50% 50%, rgba(255, 236, 220, 0.35) 0%, rgba(255, 236, 220, 0) 80%)',
  'linear-gradient(180deg, #FBFAFF 0%, #FDFBF8 100%)',
].join(', ');

export const INTAKE_GRADIENT_DARK = [
  'radial-gradient(60% 45% at 18% 22%, rgba(139, 128, 249, 0.22) 0%, rgba(139, 128, 249, 0) 70%)',
  'radial-gradient(55% 40% at 82% 18%, rgba(94, 190, 230, 0.18) 0%, rgba(94, 190, 230, 0) 72%)',
  'radial-gradient(70% 50% at 22% 88%, rgba(244, 144, 99, 0.16) 0%, rgba(244, 144, 99, 0) 72%)',
  'radial-gradient(62% 45% at 85% 92%, rgba(233, 168, 126, 0.18) 0%, rgba(233, 168, 126, 0) 72%)',
  'linear-gradient(180deg, #17171B 0%, #1C1B21 100%)',
].join(', ');

/** Lookup from intake.config icon key → lucide component. */
const ICON_MAP: Record<string, React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>> = {
  activity: Activity,
  'alert-triangle': AlertTriangle,
  'arrow-right': ArrowRight,
  'arrow-right-left': ArrowRightLeft,
  bell: Bell,
  briefcase: Briefcase,
  calendar: Calendar,
  'calendar-check': CalendarCheck,
  'check-circle': CheckCircle2,
  clipboard: Clipboard,
  'clipboard-check': ClipboardCheck,
  clock: Clock,
  'code-2': Code2,
  'file-text': FileText,
  figma: Figma,
  'flask-conical': FlaskConical,
  gift: Gift,
  handshake: Handshake,
  infinity: InfinityIcon,
  layers: Layers,
  'list-checks': ListChecks,
  mail: Mail,
  megaphone: Megaphone,
  'piggy-bank': PiggyBank,
  phone: Phone,
  'phone-call': PhoneCall,
  reply: Reply,
  repeat: Repeat,
  scale: Scale,
  send: Send,
  snowflake: Snowflake,
  sparkles: Sparkles,
  star: Star,
  'trending-up': TrendingUp,
  'user-plus': UserPlus,
  users: Users,
  'x-circle': XCircle,
};

function iconFor(name: string): React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }> {
  return ICON_MAP[name] ?? MessageSquare;
}

export interface AssistantIntakeProps {
  /** Greeting with `{firstName}` token. */
  greeting: string;
  placeholder: string;
  /** Optional pill rendered left of [Submit]. */
  scopePill?: IntakeScopePill;
  /** Exactly 4 suggestion chips; the component truncates extras. */
  suggestions: IntakeSuggestion[];
  /** Shows the [🔍 Beta] badge left of the scope pill. */
  beta?: boolean;
  /** First name token for the greeting — parent supplies from auth state. */
  firstName: string;
  /** Called on [Submit] with the textarea content + currently selected scope. */
  onSubmit: (text: string, scope: string | null) => void;
  /** §21.G — when true, `[Submit]` swaps to `[Stop]`. */
  isStreaming?: boolean;
  /** §21.G — renders meta line "Evaluating {entityPlural} (current/total)…". */
  streamingProgress?: { current: number; total: number };
  /** Clicking Stop during streaming. */
  onStop?: () => void;
  /** Entity plural for the streaming meta line. */
  entityPlural?: IntakeEntity | string;
  /**
   * Gradient variant. Only `"intake"` is allowed per §27.A. If omitted,
   * NO gradient is applied (useful when the parent wraps the intake in a
   * card surface that already supplies background tokens).
   */
  gradient?: 'intake';
  /** Optional extra rail content rendered above the intake column. */
  renderTopEnd?: () => React.ReactNode;
  renderTopStart?: () => React.ReactNode;
  /** Extra class applied to the outer wrapper. */
  className?: string;
  /**
   * 21-Apr — Smart Match unification hook. When provided, renders a
   * secondary `[+ Add another JD]` pill next to [Submit] inside the
   * composer footer. Consumer receives the current textarea content
   * and can graduate the page into the multi-JD list body while
   * preserving whatever the user has already typed.
   *
   * Passing `undefined` keeps the composer behaviour unchanged for
   * the other 12 intake routes.
   */
  addAnotherAction?: {
    label: string;
    onClick: (currentText: string) => void;
    /** Disabled when current textarea is empty. Default: false. */
    disabledWhenEmpty?: boolean;
    /**
     * 21-Apr follow-up — optional file upload companion. When provided a
     * second `[⬆ Upload]` pill renders next to the paste pill and
     * triggers a native file picker. The parent receives the chosen
     * files and is responsible for parsing + graduating the intake
     * (e.g. seeding MultiJdList or the bulk resume queue).
     */
    onUploadFiles?: (files: File[]) => void;
    /** Accept filter for the file picker. Default: JD extensions. */
    uploadAccept?: string;
    /** Upload pill label. Default: "Upload files". */
    uploadLabel?: string;
  };
}

export function AssistantIntake({
  greeting,
  placeholder,
  scopePill,
  suggestions,
  beta,
  firstName,
  onSubmit,
  isStreaming = false,
  streamingProgress,
  onStop,
  entityPlural = 'candidates',
  gradient,
  renderTopEnd,
  renderTopStart,
  className = '',
  addAnotherAction,
}: AssistantIntakeProps) {
  const [prompt, setPrompt] = useState('');
  const [scope, setScope] = useState<string | null>(() => scopePill?.options[0] ?? null);
  const [scopeMenuOpen, setScopeMenuOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  // §27.A — truncate to exactly 4 chips.
  const chips = useMemo(() => suggestions.slice(0, 4), [suggestions]);

  const greetingText = useMemo(
    () => greeting.replace('{firstName}', firstName),
    [greeting, firstName],
  );

  const canSubmit = prompt.trim().length > 0 && !isStreaming;

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Auto-grow textarea (3 → 6 rows, capped at ~168px).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
  }, [prompt]);

  const submit = useCallback(() => {
    const text = prompt.trim();
    if (!text || isStreaming) return;
    onSubmit(text, scope);
  }, [prompt, scope, isStreaming, onSubmit]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit],
  );

  /**
   * Render the same gradient in light and dark via separate absolutely-
   * positioned layers — a `html.dark .ggj-intake-gradient` rule flips
   * which layer is opaque so we don't need JS to read the theme.
   */
  const showGradient = gradient === 'intake';

  return (
    <div
      className={`relative flex min-h-0 flex-1 flex-col overflow-hidden ${className}`}
      data-ggj-assistant-intake
    >
      {showGradient ? (
        <>
          <div
            aria-hidden
            className="ggj-intake-gradient ggj-intake-gradient--light pointer-events-none absolute inset-0"
            style={{ background: INTAKE_GRADIENT_LIGHT }}
          />
          <div
            aria-hidden
            className="ggj-intake-gradient ggj-intake-gradient--dark pointer-events-none absolute inset-0"
            style={{ background: INTAKE_GRADIENT_DARK }}
          />
        </>
      ) : null}
      <div className="relative flex min-h-0 flex-1 flex-col">
      {(renderTopStart || renderTopEnd) && (
        <div className="flex items-start justify-between px-6 pt-6">
          <div>{renderTopStart?.()}</div>
          <div>{renderTopEnd?.()}</div>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-8">
        <div className="flex w-full max-w-[640px] flex-col items-center">
          {/* Greeting row — branded AgentMark + 22 px greeting */}
          <div className="mb-6 flex items-center gap-2">
            <AgentMark className="h-5 w-5" withHalo ariaLabel={null} />
            <h1
              className="text-center text-[22px] font-semibold leading-tight"
              style={{ color: 'var(--vscode-text-primary)' }}
            >
              {greetingText}
            </h1>
          </div>

          {/* Intake card */}
          <div
            className="w-full rounded-[12px] border"
            style={{
              background: 'var(--vscode-bg-elevated)',
              borderColor: 'var(--vscode-border-primary)',
            }}
          >
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={onKeyDown}
              rows={3}
              placeholder={placeholder}
              disabled={isStreaming}
              className="min-h-[92px] w-full resize-none rounded-t-[12px] bg-transparent px-4 py-3 text-[15px] leading-relaxed outline-none placeholder:text-[color:var(--vscode-text-tertiary)] disabled:opacity-60"
              style={{ color: 'var(--vscode-text-primary)', maxHeight: 168 }}
              aria-label="Assistant prompt"
            />

            <div
              className="flex flex-wrap items-center gap-2 border-t px-3 py-2"
              style={{ borderColor: 'var(--vscode-border-primary)' }}
            >
              {beta ? (
                <span
                  className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium"
                  style={{
                    borderColor: 'var(--vscode-border-primary)',
                    color: 'var(--vscode-text-tertiary)',
                  }}
                  title="This experience is in beta"
                >
                  <SearchIcon className="h-3 w-3" aria-hidden />
                  Beta
                </span>
              ) : null}

              {scopePill ? (
                <>
                  <span className="text-[11px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
                    {scopePill.label}
                  </span>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setScopeMenuOpen((v) => !v)}
                      className="inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-[11px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
                      style={{
                        borderColor: 'var(--vscode-border-primary)',
                        color: 'var(--vscode-text-secondary)',
                      }}
                      aria-haspopup="listbox"
                      aria-expanded={scopeMenuOpen}
                    >
                      {scope ?? scopePill.options[0]}
                      <CornerDownLeft className="h-3 w-3 rotate-90" aria-hidden />
                    </button>
                    {scopeMenuOpen ? (
                      <>
                        <div
                          className="fixed inset-0 z-30"
                          onClick={() => setScopeMenuOpen(false)}
                          aria-hidden
                        />
                        <div
                          className="absolute bottom-full left-0 z-40 mb-1 min-w-[160px] rounded-[8px] border py-1 shadow-lg"
                          style={{
                            background: 'var(--vscode-bg-elevated)',
                            borderColor: 'var(--vscode-border-primary)',
                          }}
                          role="listbox"
                        >
                          {scopePill.options.map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              role="option"
                              aria-selected={scope === opt}
                              onClick={() => {
                                setScope(opt);
                                setScopeMenuOpen(false);
                              }}
                              className={`flex w-full items-center px-3 py-1.5 text-left text-[12px] ${
                                scope === opt
                                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                                  : 'hover:bg-[var(--vscode-bg-hover)]'
                              }`}
                              style={{ color: scope === opt ? undefined : 'var(--vscode-text-secondary)' }}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : null}
                  </div>
                </>
              ) : null}

              <div className="ml-auto flex items-center gap-2">
                {addAnotherAction && !isStreaming ? (
                  <button
                    type="button"
                    onClick={() => addAnotherAction.onClick(prompt.trim())}
                    disabled={
                      addAnotherAction.disabledWhenEmpty && prompt.trim().length === 0
                    }
                    className="inline-flex h-8 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                    style={{
                      borderColor: 'var(--vscode-border-primary)',
                      color: 'var(--vscode-text-secondary)',
                    }}
                    title={
                      addAnotherAction.disabledWhenEmpty && prompt.trim().length === 0
                        ? 'Paste or type a JD first'
                        : addAnotherAction.label
                    }
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden />
                    {addAnotherAction.label}
                  </button>
                ) : null}
                {addAnotherAction?.onUploadFiles && !isStreaming ? (
                  <>
                    <input
                      ref={uploadInputRef}
                      type="file"
                      multiple
                      accept={
                        addAnotherAction.uploadAccept ??
                        '.txt,.md,.pdf,.doc,.docx'
                      }
                      className="sr-only"
                      onChange={(e) => {
                        const list = e.target.files;
                        if (!list || list.length === 0) return;
                        const files = Array.from(list);
                        addAnotherAction.onUploadFiles?.(files);
                        e.target.value = '';
                      }}
                      aria-label="Upload files"
                    />
                    <button
                      type="button"
                      onClick={() => uploadInputRef.current?.click()}
                      className="inline-flex h-8 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
                      style={{
                        borderColor: 'var(--vscode-border-primary)',
                        color: 'var(--vscode-text-secondary)',
                      }}
                      title="Upload JD or resume files (PDF, DOC, DOCX, TXT)"
                    >
                      <Upload className="h-3.5 w-3.5" aria-hidden />
                      {addAnotherAction.uploadLabel ?? 'Upload files'}
                    </button>
                  </>
                ) : null}
                <span
                  className="hidden text-[11px] sm:inline"
                  style={{ color: 'var(--vscode-text-tertiary)' }}
                >
                  ↵ to send · Shift+↵ for newline
                </span>
                {isStreaming ? (
                  /* §21.G — red-soft Stop button. */
                  <button
                    type="button"
                    onClick={() => onStop?.()}
                    className="inline-flex h-9 items-center gap-1.5 rounded-[10px] bg-red-500/15 px-4 text-[13px] font-medium text-red-600 transition-colors hover:bg-red-500/25 dark:text-red-300"
                  >
                    <Square className="h-3.5 w-3.5 fill-current" aria-hidden />
                    Stop
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={submit}
                    disabled={!canSubmit}
                    className="inline-flex h-9 items-center gap-1.5 rounded-[10px] bg-emerald-500 px-4 text-[13px] font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Submit
                    <CornerDownLeft className="h-3.5 w-3.5" aria-hidden />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* §21.G streaming progress meta line */}
          {isStreaming && streamingProgress ? (
            <p
              className="mt-3 text-[13px]"
              style={{ color: 'var(--vscode-text-secondary)' }}
              aria-live="polite"
            >
              Evaluating {entityPlural} ({streamingProgress.current}/{streamingProgress.total})…
            </p>
          ) : null}

          {/*
           * Suggestion chips — exactly 4. For routes that ship JD
           * templates (e.g. `/smart-match/new`) the chip carries an
           * optional `prompt` body; clicking drops that full body into
           * the composer. For all other routes the chip label itself
           * seeds the textarea (legacy behaviour).
           */}
          <div className="mt-6 flex w-full flex-wrap items-center justify-center gap-2">
            {chips.map((chip) => {
              const Icon = iconFor(chip.icon);
              const seed = chip.prompt ?? chip.label;
              return (
                <button
                  key={chip.label}
                  type="button"
                  disabled={isStreaming}
                  onClick={() => {
                    setPrompt(seed);
                    textareaRef.current?.focus();
                  }}
                  title={chip.description ?? chip.label}
                  className="inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[11px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)] disabled:opacity-50"
                  style={{
                    borderColor: 'var(--vscode-border-primary)',
                    color: 'var(--vscode-text-secondary)',
                  }}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
